import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowRight, ArrowLeft, CheckCircle, Phone } from 'lucide-react';
import axios from 'axios';
import { PrequalInfo, PrequalHero, PrequalCta } from '../components/PrequalInfo';

const API = '/api';

const steps = [
  { id: 'credit', title_fr: 'Votre situation de crédit', title_en: 'Your Credit Situation' },
  { id: 'employment', title_fr: 'Emploi et revenu', title_en: 'Employment & Income' },
  { id: 'vehicle', title_fr: 'Véhicule recherché', title_en: 'Vehicle Desired' },
  { id: 'contact', title_fr: 'Vos coordonnées', title_en: 'Your Contact Info' },
];

const EligibilityTool = () => {
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState({
    credit_situation: '',
    bankruptcy: '',
    employment_status: '',
    monthly_income: '',
    time_at_job: '',
    vehicle_type: '',
    budget: '',
    name: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    document.title = language === 'en'
      ? 'Quick Car Loan Pre-Qualification | No Credit Impact | Credit Auto Reset'
      : 'Préqualification de prêt auto rapide | Sans impact sur votre crédit | Credit Auto Reset';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      language === 'en'
        ? 'Check if you qualify for auto financing in 30 seconds. Free, no obligation, no impact on your credit score. Bad credit, bankruptcy accepted.'
        : 'Verifiez si vous qualifiez pour un financement auto en 30 secondes. Gratuit, sans obligation, aucun impact sur votre cote de credit.'
    );
  }, [language]);

  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const calculateScore = () => {
    let score = 50;
    if (data.credit_situation === 'good') score += 30;
    else if (data.credit_situation === 'fair') score += 15;
    else if (data.credit_situation === 'poor') score += 5;
    else if (data.credit_situation === 'very_poor') score += 0;
    if (data.bankruptcy === 'no') score += 15;
    else if (data.bankruptcy === 'discharged') score += 8;
    else score += 3;
    if (data.employment_status === 'full_time') score += 15;
    else if (data.employment_status === 'part_time') score += 10;
    else if (data.employment_status === 'self_employed') score += 8;
    else score += 3;
    if (parseInt(data.monthly_income) >= 3000) score += 10;
    else if (parseInt(data.monthly_income) >= 2000) score += 5;
    return Math.min(score, 99);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        name: data.name,
        phone: data.phone,
        email: data.email,
        source: 'Eligibility Tool',
        notes: `Credit: ${data.credit_situation}, Bankruptcy: ${data.bankruptcy}, Employment: ${data.employment_status}, Income: ${data.monthly_income}$/mois, Budget: ${data.budget}$/mois, Vehicle: ${data.vehicle_type}`,
        score: calculateScore()
      });
    } catch (e) {}
    setIsSubmitting(false);
    setShowResult(true);
  };

  const OptionButton = ({ value, current, onClick, children }) => (
    <button
      onClick={() => onClick(value)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-[border-color,background-color,transform] hover:-translate-y-0.5 ${
        current === value
          ? 'border-[#1a5c3b] bg-[#1a5c3b]/5 text-[#1a5c3b] font-semibold'
          : 'border-gray-200 hover:border-[#1a5c3b]/50 text-gray-700'
      }`}
    >
      {children}
    </button>
  );

  if (showResult) {
    const score = calculateScore();
    return (
      <div className="min-h-screen pt-24 md:pt-[120px] bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Great News!' : 'Excellente nouvelle!'}
            </h1>
            <div className="bg-gradient-to-r from-[#1a5c3b] to-[#16a085] rounded-2xl p-6 mb-6">
              <p className="text-white/80 text-sm mb-1">{language === 'en' ? 'Your eligibility score' : 'Votre score d\'admissibilité'}</p>
              <p className="text-5xl font-bold text-white">{score}%</p>
              <p className="text-green-200 text-sm mt-1">{language === 'en' ? 'Chances of approval' : 'Chances d\'approbation'}</p>
            </div>
            <p className="text-gray-600 mb-6">
              {language === 'en'
                ? 'Based on your answers, you have excellent chances of getting approved! Our credit analyst will review your file and contact you within 24 hours with your options.'
                : 'Selon vos reponses, vous avez d\'excellentes chances d\'etre approuve! Notre analyste de credit va examiner votre dossier et vous contacter dans les 24 heures avec vos options.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/demander-un-pret-auto">
                <Button size="lg" className="bg-[#1a5c3b] hover:bg-[#16a085] text-white font-bold px-8">
                  {language === 'en' ? 'Complete Full Application' : 'Compléter la demande'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:514-418-3968">
                <Button size="lg" variant="outline" className="font-bold px-8">
                  <Phone className="mr-2 w-5 h-5" />
                  514-418-3968
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 md:pt-[120px] bg-white">
      <PrequalHero />

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-4 -mt-10 md:-mt-14 relative z-10 scroll-mt-32" data-testid="prequal-form-anchor">
        <div className="bg-white rounded-t-3xl border border-b-0 border-emerald-100 border-t-4 border-t-[#1a5c3b] px-6 md:px-10 pt-7 pb-2 shadow-[0_-12px_40px_rgba(26,92,59,0.08)]">
          <div className="flex gap-2 mb-3">
            {steps.map((s, i) => (
              <div key={i} className={`flex-1 h-2 rounded-full transition-colors duration-500 ${i <= currentStep ? 'bg-gradient-to-r from-[#1a5c3b] to-emerald-500' : 'bg-gray-100'}`} />
            ))}
          </div>
          <p className="text-sm text-gray-500" data-testid="prequal-step-label">
            {language === 'en' ? `Step ${currentStep + 1} of ${steps.length}` : `Étape ${currentStep + 1} de ${steps.length}`} — <span className="font-semibold text-[#1a5c3b]">{language === 'en' ? steps[currentStep].title_en : steps[currentStep].title_fr}</span>
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 pb-8" data-testid="prequal-form">
        <Card className="rounded-t-none rounded-b-3xl border border-t-0 border-emerald-100 shadow-[0_30px_60px_rgba(26,92,59,0.12)]">
          <CardContent className="p-6 md:p-10 pt-4 md:pt-6">
            {currentStep === 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{language === 'en' ? 'How would you describe your credit?' : 'Comment décririez-vous votre crédit?'}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <OptionButton value="good" current={data.credit_situation} onClick={(v) => updateData('credit_situation', v)}>
                    {language === 'en' ? 'Good (650+)' : 'Bon (650+)'}
                  </OptionButton>
                  <OptionButton value="fair" current={data.credit_situation} onClick={(v) => updateData('credit_situation', v)}>
                    {language === 'en' ? 'Fair (550-649)' : 'Moyen (550-649)'}
                  </OptionButton>
                  <OptionButton value="poor" current={data.credit_situation} onClick={(v) => updateData('credit_situation', v)}>
                    {language === 'en' ? 'Poor (400-549)' : 'Mauvais (400-549)'}
                  </OptionButton>
                  <OptionButton value="very_poor" current={data.credit_situation} onClick={(v) => updateData('credit_situation', v)}>
                    {language === 'en' ? 'Very poor or I don\'t know' : 'Très mauvais ou je ne sais pas'}
                  </OptionButton>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mt-6 mb-4">{language === 'en' ? 'Have you ever declared bankruptcy?' : 'Avez-vous déjà fait faillite?'}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <OptionButton value="no" current={data.bankruptcy} onClick={(v) => updateData('bankruptcy', v)}>
                    {language === 'en' ? 'No, never' : 'Non, jamais'}
                  </OptionButton>
                  <OptionButton value="discharged" current={data.bankruptcy} onClick={(v) => updateData('bankruptcy', v)}>
                    {language === 'en' ? 'Yes, discharged' : 'Oui, libérée'}
                  </OptionButton>
                  <OptionButton value="active" current={data.bankruptcy} onClick={(v) => updateData('bankruptcy', v)}>
                    {language === 'en' ? 'Yes, currently active' : 'Oui, en cours'}
                  </OptionButton>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{language === 'en' ? 'What is your employment status?' : 'Quel est votre statut d\'emploi?'}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <OptionButton value="full_time" current={data.employment_status} onClick={(v) => updateData('employment_status', v)}>
                    {language === 'en' ? 'Full-time employee' : 'Employé temps plein'}
                  </OptionButton>
                  <OptionButton value="part_time" current={data.employment_status} onClick={(v) => updateData('employment_status', v)}>
                    {language === 'en' ? 'Part-time employee' : 'Employé temps partiel'}
                  </OptionButton>
                  <OptionButton value="self_employed" current={data.employment_status} onClick={(v) => updateData('employment_status', v)}>
                    {language === 'en' ? 'Self-employed' : 'Travailleur autonome'}
                  </OptionButton>
                  <OptionButton value="other" current={data.employment_status} onClick={(v) => updateData('employment_status', v)}>
                    {language === 'en' ? 'Other (disability, pension, EI, etc.)' : 'Autre (invalidité, pension, AE, etc.)'}
                  </OptionButton>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mt-6 mb-4">{language === 'en' ? 'Monthly income (before taxes)' : 'Revenu mensuel (avant impôts)'}</h2>
                <Input
                  type="number"
                  placeholder={language === 'en' ? 'e.g. 3500' : 'ex: 3500'}
                  value={data.monthly_income}
                  onChange={(e) => updateData('monthly_income', e.target.value)}
                  className="text-lg py-3"
                />
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{language === 'en' ? 'What type of vehicle are you looking for?' : 'Quel type de véhicule recherchez-vous?'}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { val: 'sedan', fr: 'Berline', en: 'Sedan' },
                    { val: 'suv', fr: 'VUS / SUV', en: 'SUV' },
                    { val: 'pickup', fr: 'Camion / Pickup', en: 'Pickup / Truck' },
                    { val: 'compact', fr: 'Compact', en: 'Compact' },
                    { val: 'minivan', fr: 'Mini-fourgonnette', en: 'Minivan' },
                    { val: 'any', fr: 'Peu importe', en: 'No preference' },
                  ].map(opt => (
                    <OptionButton key={opt.val} value={opt.val} current={data.vehicle_type} onClick={(v) => updateData('vehicle_type', v)}>
                      {language === 'en' ? opt.en : opt.fr}
                    </OptionButton>
                  ))}
                </div>
                <h2 className="text-xl font-bold text-gray-900 mt-6 mb-4">{language === 'en' ? 'Monthly budget for the vehicle' : 'Budget mensuel pour le véhicule'}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { val: '300', label: '300$ - 400$ / mois' },
                    { val: '500', label: '400$ - 600$ / mois' },
                    { val: '700', label: '600$ - 800$ / mois' },
                    { val: '900', label: '800$+ / mois' },
                  ].map(opt => (
                    <OptionButton key={opt.val} value={opt.val} current={data.budget} onClick={(v) => updateData('budget', v)}>
                      {opt.label}
                    </OptionButton>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{language === 'en' ? 'Almost done! Your contact info:' : 'Presque terminé! Vos coordonnées:'}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'en' ? 'Full Name' : 'Nom complet'}</label>
                    <Input value={data.name} onChange={(e) => updateData('name', e.target.value)} placeholder={language === 'en' ? 'John Smith' : 'Jean Dupont'} className="text-lg py-3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'en' ? 'Phone' : 'Téléphone'}</label>
                    <Input value={data.phone} onChange={(e) => updateData('phone', e.target.value)} placeholder="514-555-1234" className="text-lg py-3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'en' ? 'Email' : 'Courriel'}</label>
                    <Input type="email" value={data.email} onChange={(e) => updateData('email', e.target.value)} placeholder="email@exemple.com" className="text-lg py-3" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  {language === 'en'
                    ? 'Your information is 100% confidential. No impact on your credit score.'
                    : 'Vos informations sont 100% confidentielles. Aucun impact sur votre cote de credit.'}
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              {currentStep > 0 ? (
                <Button variant="outline" className="rounded-full px-6 py-5" onClick={() => setCurrentStep(currentStep - 1)}>
                  <ArrowLeft className="mr-2 w-4 h-4" />{language === 'en' ? 'Back' : 'Retour'}
                </Button>
              ) : <div />}
              {currentStep < steps.length - 1 ? (
                <Button className="bg-[#1a5c3b] hover:bg-[#165432] rounded-full px-7 py-5 text-base shadow-lg shadow-[#1a5c3b]/20" onClick={() => setCurrentStep(currentStep + 1)}>
                  {language === 'en' ? 'Next' : 'Suivant'}<ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button className="bg-[#1a5c3b] hover:bg-[#165432] rounded-full px-7 py-5 text-base shadow-lg shadow-[#1a5c3b]/20" onClick={handleSubmit} disabled={isSubmitting || !data.name || !data.phone}>
                  {isSubmitting ? '...' : (language === 'en' ? 'See My Result' : 'Voir mon résultat')}
                  <CheckCircle className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <PrequalInfo />
      <PrequalCta />
    </div>
  );
};

export default EligibilityTool;
